/**
 * QrCode.js
 *
 * A QR code drawn as SVG by zxing-wasm's writer (the same library as the counter's scanner).
 * Its WebAssembly comes from this site (public/zxing), not a CDN.
 */

import React, { useEffect, useState } from "react";
import { setZXingModuleOverrides, writeBarcode } from "zxing-wasm/writer";

setZXingModuleOverrides({
    locateFile: (path, prefix) => (path.endsWith(".wasm") ? "/zxing/zxing_writer-3.1.3.wasm" : prefix + path),
});

/**
 * @param {String} value    -   What the code holds, e.g. a PNR or a link
 * @param {Number} size     -   Width and height in px
 * @param {Function} onReady    -   Called once the code is drawn (e.g. to capture it as an image)
 */
const QrCode = ({ value, size = 96, onReady }) => {
    const [svg, setSvg] = useState("");
    useEffect(() => {
        let ignore = false;
        writeBarcode(value, { format: "QRCode" })
            // The writer's SVG has a fixed size and no viewBox: give it one, so it scales to the box (sharp edges for scanners)
            .then((result) => {
                if (ignore || result.error) return;
                setSvg(result.svg.replace(/<svg width="(\d+)" height="(\d+)"/, '<svg viewBox="0 0 $1 $2" width="100%" height="100%" shape-rendering="crispEdges"'));
                // After the browser has painted it
                requestAnimationFrame(() => onReady?.());
            })
            .catch((err) => console.error("Couldn't draw a QR code:", err));
        return () => { ignore = true; };
        // onReady is a callback: only redraw when the value changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);
    return (
        <span
            className="block bg-white"
            style={{ width: size, height: size }}
            role="img"
            aria-label={`QR code for ${value}`}
            // Our own QR writer's output for our own value
            dangerouslySetInnerHTML={{ __html: svg }}
        />
    );
};

export default QrCode;
